import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaFolderOpen, FaPlus, FaExternalLinkAlt, FaCheckCircle, FaSearch } from 'react-icons/fa';
import { documentsAPI } from '../services/api';
import { Modal, ModalFooter } from '../components/Modals';
import CvDocumentsPanel from '../components/CvDocumentsPanel';

const DOCUMENT_TYPES = ['VMP', 'SOP', 'Protocol', 'Report', 'Policy', 'Other'];

// Related records are stored as a loose (table, id) pair rather than real foreign keys —
// this maps the table name to where that record actually lives in the app, so "Open" can
// navigate there. Add an entry here if a new related_table value starts being used.
const RELATED_TABLE_ROUTES = {
  sampling_plans: { path: '/acceptance-criteria', label: 'Sampling Plan' },
  equipment_master: { path: '/equipment-master', label: 'Equipment' },
  equipment_train: { path: '/equipment-trains', label: 'Equipment Train' },
  product_master: { path: '/product-master', label: 'Product' },
  api_master: { path: '/api-master', label: 'API' },
  cv_documents: { path: '/documents', label: 'CV Protocol / Report' },
};

const EMPTY_FORM = {
  document_type: 'SOP',
  document_number: '',
  document_title: '',
  version: '',
  related_table: '',
  related_id: '',
};

const getRows = (response) => {
  const data = response?.data?.data;
  return Array.isArray(data) ? data : [];
};

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await documentsAPI.getAll();
      setDocuments(getRows(res));
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return documents.filter((doc) => {
      if (typeFilter !== 'All' && doc.document_type !== typeFilter) return false;
      if (!q) return true;
      return [doc.document_number, doc.document_title, doc.version].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [documents, search, typeFilter]);

  const openNew = () => { setForm(EMPTY_FORM); setModalOpen(true); };

  const save = async () => {
    if (!form.document_number.trim() || !form.document_title.trim()) {
      toast.warning('Document number and title are required');
      return;
    }
    setSaving(true);
    try {
      await documentsAPI.create({
        ...form,
        related_table: form.related_table || null,
        related_id: form.related_id || null,
      });
      toast.success('Document registered');
      setModalOpen(false);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to register document');
    } finally {
      setSaving(false);
    }
  };

  const relatedLink = (doc) => {
    const route = RELATED_TABLE_ROUTES[doc.related_table];
    if (!route || !doc.related_id) return null;
    return (
      <Link to={route.path} title={`Open related ${route.label}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: 'var(--fs-sm)' }}>
        {route.label} #{doc.related_id} <FaExternalLinkAlt style={{ fontSize: 'var(--fs-2xs)' }} />
      </Link>
    );
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '14px', marginBottom: '18px' }}>
        <div>
          <h1 style={{ margin: '0 0 5px', fontSize: 'var(--fs-xl)', display: 'flex', alignItems: 'center', gap: '8px' }}><FaFolderOpen /> Documents</h1>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            Protocols, reports, SOPs and other controlled documents, with version and effective status.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openNew}><FaPlus /> Register Document</button>
      </div>

      <CvDocumentsPanel />

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <strong>Document Register</strong>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
              <input className="form-control" style={{ paddingLeft: '30px', minWidth: '220px' }} placeholder="Search number, title, version..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select className="form-control" style={{ width: 'auto' }} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="All">All Types</option>
              {DOCUMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Number</th>
                <th>Title</th>
                <th>Version</th>
                <th>Related Record</th>
                <th>Status</th>
                <th>Created By</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-muted">No documents registered yet.</td></tr>
              ) : filtered.map((doc) => (
                <tr key={doc.document_id}>
                  <td>{doc.document_type}</td>
                  <td style={{ fontWeight: 600 }}>{doc.document_number}</td>
                  <td>{doc.document_title}</td>
                  <td>{doc.version || '-'}</td>
                  <td>{relatedLink(doc) || <span className="text-muted">-</span>}</td>
                  <td>
                    {doc.status && doc.status !== 'Effective' && !doc.is_effective ? (
                      <span className="text-muted">{doc.status}</span>
                    ) : doc.is_effective ? (
                      <span style={{ color: 'var(--success-text)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><FaCheckCircle /> Effective</span>
                    ) : (
                      <span className="text-muted">Draft</span>
                    )}
                    {doc.approved_by_name && (
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>by {doc.approved_by_name}{doc.approved_at ? ` on ${new Date(doc.approved_at).toLocaleDateString()}` : ''}</div>
                    )}
                  </td>
                  <td>{doc.created_by_name || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Register Document" size="md">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Type</label>
            <select className="form-control" value={form.document_type} onChange={(e) => setForm((p) => ({ ...p, document_type: e.target.value }))}>
              {DOCUMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Document Number *</label>
            <input className="form-control" value={form.document_number} onChange={(e) => setForm((p) => ({ ...p, document_number: e.target.value }))} placeholder="e.g. SOP-QA-014" />
          </div>
          <div className="form-group form-group-wide">
            <label className="form-label">Title *</label>
            <input className="form-control" value={form.document_title} onChange={(e) => setForm((p) => ({ ...p, document_title: e.target.value }))} placeholder="Document title" />
          </div>
          <div className="form-group">
            <label className="form-label">Version</label>
            <input className="form-control" value={form.version} onChange={(e) => setForm((p) => ({ ...p, version: e.target.value }))} placeholder="e.g. 01" />
          </div>
        </div>
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
          This registers the document's identity (number, title, version) in the system — it does not store a file. Keep the actual controlled copy in your document management system or SOP repository; this register is for traceability.
        </p>
        <ModalFooter>
          <button className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Register'}</button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Documents;
